import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PaperPlaneRight, Bell, Check, CircleNotch, Paperclip, Download, Trash, FileText, X } from "@phosphor-icons/react";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

export default function Notificaciones() {
  const { user } = useAuth();
  const [notifs, setNotifs] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [attachments, setAttachments] = useState(null);
  const [form, setForm] = useState({ title: "", message: "", employee_id: "all" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    Promise.allSettled([api.get("/notifications"), api.get("/employees"), localApi.get("/attachments")]).then(
      ([n, e, a]) => {
        setNotifs(n.status === "fulfilled" && Array.isArray(n.value.data) ? n.value.data : []);
        setEmployees(e.status === "fulfilled" && Array.isArray(e.value.data) ? e.value.data : []);
        setAttachments(a.status === "fulfilled" && Array.isArray(a.value.data) ? a.value.data : []);
        if (n.status === "rejected") toast.error(apiError(n.reason));
      }
    );
  }, []);

  useEffect(load, [load]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      let message = form.message;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("employee_id", form.employee_id === "all" ? "" : form.employee_id);
        await localApi.post("/attachments", fd);
        message = `${form.message}\n\nArchivo adjunto: ${file.name} — disponible en el portal web, sección Notificaciones.`;
      }
      await api.post("/notifications", {
        title: form.title,
        message,
        employee_id: form.employee_id === "all" ? null : form.employee_id,
      });
      toast.success(form.employee_id === "all" ? "Notificación enviada a todos" : "Notificación enviada");
      setForm({ title: "", message: "", employee_id: "all" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const downloadAttachment = async (a) => {
    try {
      const res = await localApi.get(`/attachments/${a.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = a.original_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeAttachment = async (a) => {
    try {
      await localApi.delete(`/attachments/${a.id}`);
      toast.success("Archivo eliminado");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const empName = (id) => employees.find((e) => e.id === id)?.full_name || "Empleado";

  const markRead = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!notifs) return <Loading label="Cargando notificaciones..." />;

  return (
    <div data-testid="notificaciones-page">
      <PageHeader title="Notificaciones" description="Comunicados para los colaboradores en la app móvil" testid="notificaciones-header" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {user?.role === "admin" && (
        <div className="border border-border bg-card rounded-sm p-5 h-fit" data-testid="send-card">
          <h3 className="font-heading text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <PaperPlaneRight size={18} className="text-muted-foreground" /> Enviar notificación
          </h3>
          <form onSubmit={send} className="space-y-4" data-testid="notification-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Destinatario</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className={inputCls} data-testid="notif-recipient-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">Todos los empleados</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Título *</Label>
              <Input className={inputCls} required data-testid="notif-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mensaje *</Label>
              <Textarea className={`${inputCls} min-h-[100px]`} required data-testid="notif-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Adjuntar archivo (opcional)</Label>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                data-testid="notif-file-input"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.docx,.txt,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file ? (
                <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2" data-testid="notif-file-chip">
                  <span className="text-sm text-foreground/80 truncate flex items-center gap-2">
                    <Paperclip size={14} className="text-muted-foreground shrink-0" /> {file.name}
                  </span>
                  <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="notif-file-remove">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                  className="w-full rounded-sm border-dashed border-input bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground" data-testid="notif-attach-button">
                  <Paperclip className="mr-2" size={15} /> Seleccionar archivo (máx. 10 MB)
                </Button>
              )}
            </div>
            <Button type="submit" disabled={sending} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="notif-send-button">
              {sending ? <CircleNotch className="animate-spin mr-2" size={15} /> : <PaperPlaneRight className="mr-2" size={15} />}
              Enviar
            </Button>
          </form>
        </div>
        )}

        <div className={`${user?.role === "admin" ? "lg:col-span-2" : "lg:col-span-3"} space-y-3`} data-testid="notifications-list">
          {notifs.length === 0 ? (
            <EmptyState title="Sin notificaciones" description="Los comunicados enviados aparecerán aquí." />
          ) : (
            notifs.map((n) => (
              <div key={n.id} className={`border rounded-sm p-4 transition-colors duration-200 ${n.read ? "border-border bg-background" : "border-border bg-card"}`} data-testid={`notif-${n.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Bell size={16} className={`mt-0.5 shrink-0 ${n.read ? "text-muted-foreground" : "text-foreground"}`} />
                    <div>
                      <p className={`text-sm font-medium ${n.read ? "text-muted-foreground" : "text-foreground"}`}>{n.title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">{new Date(n.created_at).toLocaleString("es-HN")}</p>
                    </div>
                  </div>
                  {n.read ? (
                    <Badge variant="outline" className="rounded-sm border-border text-muted-foreground font-normal shrink-0">Leída</Badge>
                  ) : (
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0" data-testid={`notif-read-${n.id}`} onClick={() => markRead(n)}>
                      <Check className="mr-1" size={14} /> Marcar leída
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-6 border border-border bg-card rounded-sm" data-testid="attachments-card">
        <h3 className="font-heading text-lg font-bold text-foreground p-5 pb-4 flex items-center gap-2">
          <Paperclip size={18} className="text-muted-foreground" /> Archivos compartidos
        </h3>
        {!attachments ? (
          <Loading />
        ) : attachments.length === 0 ? (
          <EmptyState title="Sin archivos" description="Los archivos adjuntos a las notificaciones aparecerán aquí." />
        ) : (
          <div className="divide-y divide-border border-t border-border">
            {attachments.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`attachment-${a.id}`}>
                <FileText size={18} className="text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground truncate">{a.original_filename}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {a.title} · {a.employee_id ? empName(a.employee_id) : "Todos los empleados"} · {new Date(a.created_at).toLocaleString("es-HN")} · {(a.size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`attachment-download-${a.id}`} onClick={() => downloadAttachment(a)}>
                  <Download className="mr-1.5" size={14} /> Descargar
                </Button>
                {user?.role === "admin" && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" data-testid={`attachment-delete-${a.id}`} onClick={() => removeAttachment(a)}>
                    <Trash size={15} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
